import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAccount, useConnect, useDisconnect, useSignMessage } from "wagmi";
import { useAuth } from "../../context/AuthContext";
import AuthModal from "../auth/AuthModal";
import { formatShortAddress, primaryLinkedWallet, shortAccountLabel } from "../../utils/walletDisplay";
import { SITE_DASHBOARD_PATH } from "../../constants/siteNavRoutes";

const SIGN_IN_HELPER_COPY =
  "Sign a message to enter Treydora. This does not cost gas and does not give permission to move funds.";
const LINK_HELPER_COPY =
  "Sign a message to link your wallet. This does not cost gas and does not give permission to move funds.";

function hasVisibleLabel(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function AuthActionButton({ label, className, disabled, onClick, type = "button" }) {
  const visibleLabel = hasVisibleLabel(label) ? label.trim() : "";
  if (!visibleLabel) {
    return null;
  }

  return (
    <button
      type={type}
      className={className}
      disabled={disabled}
      onClick={onClick}
      aria-label={visibleLabel}
    >
      {visibleLabel}
    </button>
  );
}

function ConnectWalletButton({ label, className, disabled, onClick, placement }) {
  const safeLabel = hasVisibleLabel(label) ? label.trim() : "Connect Wallet";
  if (!safeLabel) {
    return null;
  }

  if (placement === "desktop" && safeLabel === "Connect Wallet") {
    return (
      <button
        type="button"
        className={`${className} td-nav-auth__btn--connect`.trim()}
        disabled={disabled}
        onClick={onClick}
        aria-label="Connect Wallet"
      >
        <span className="td-nav-auth__label td-nav-auth__label--full" aria-hidden="true">
          Connect Wallet
        </span>
        <span className="td-nav-auth__label td-nav-auth__label--short" aria-hidden="true">
          Wallet
        </span>
      </button>
    );
  }

  return (
    <AuthActionButton
      label={safeLabel}
      className={className}
      disabled={disabled}
      onClick={onClick}
    />
  );
}

/**
 * Right-rail auth + wallet controls (Treydora account + wagmi; no Thirdweb).
 */
export default function TreydoraNavAuth({ className = "", placement = "desktop" }) {
  const {
    user,
    isAuthenticated,
    logout,
    refreshFromStorage,
    walletLogin,
    linkWallet,
  } = useAuth();
  const { address, isConnected } = useAccount();
  const { connectAsync, connectors } = useConnect();
  const { signMessageAsync } = useSignMessage();
  const { disconnect } = useDisconnect();

  const [authOpen, setAuthOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState("");
  const [err, setErr] = useState("");

  const linked = useMemo(() => primaryLinkedWallet(user), [user]);
  const shortLinked = linked?.address ? formatShortAddress(linked.address) : "";
  const needsLinkWallet = Boolean(isAuthenticated && user?.email && !linked?.address);

  const identity = useMemo(() => shortAccountLabel(user), [user]);
  const connectWalletLabel = busy ? "Signing…" : "Connect Wallet";
  const safeConnectWalletLabel = hasVisibleLabel(connectWalletLabel) ? connectWalletLabel.trim() : "Connect Wallet";
  const linkWalletLabel = busy ? "Signing…" : "Link Wallet";
  const safeLinkWalletLabel = hasVisibleLabel(linkWalletLabel) ? linkWalletLabel.trim() : "Link Wallet";
  const isDrawer = placement === "drawer";

  const ensureConnectedAddress = async () => {
    let activeAddress = address;
    if (!isConnected || !activeAddress) {
      const list = Array.isArray(connectors) ? connectors : [];
      const connector = list.find((c) => c && c.ready) || list[0];
      if (!connector) {
        throw new Error(
          "No wallet connector available. Install a browser wallet or enable WalletConnect."
        );
      }
      const connected = await connectAsync({ connector });
      activeAddress = connected?.account;
    }
    if (!activeAddress) {
      throw new Error("Wallet not connected.");
    }
    return activeAddress;
  };

  const runWalletConnectLogin = async () => {
    setBusy(true);
    setErr("");
    setHint("");
    try {
      const activeAddress = await ensureConnectedAddress();
      setHint(SIGN_IN_HELPER_COPY);
      await walletLogin({
        walletAddress: activeAddress,
        signMessageAsync,
      });
      setHint("");
      if (typeof window !== "undefined") {
        window.location.assign(SITE_DASHBOARD_PATH);
      }
    } catch (e) {
      const msg =
        e?.shortMessage ||
        e?.message ||
        (typeof e === "string" ? e : "Unable to sign in with this wallet.");
      if (/rejected|denied|user rejected/i.test(String(msg))) {
        setErr("Signature was rejected in the wallet.");
      } else {
        setErr(String(msg));
      }
    } finally {
      setBusy(false);
      setHint("");
    }
  };

  const runHeaderLinkWallet = async () => {
    setBusy(true);
    setErr("");
    setHint("");
    try {
      const activeAddress = await ensureConnectedAddress();
      setHint(LINK_HELPER_COPY);
      await linkWallet({
        walletAddress: activeAddress,
        signMessageAsync,
      });
      setHint("");
      refreshFromStorage();
    } catch (e) {
      const msg =
        e?.shortMessage ||
        e?.message ||
        (typeof e === "string" ? e : "Unable to link this wallet.");
      if (/rejected|denied|user rejected/i.test(String(msg))) {
        setErr("Signature was rejected in the wallet.");
      } else {
        setErr(String(msg));
      }
    } finally {
      setBusy(false);
      setHint("");
    }
  };

  const rootClassName = `td-nav-auth${isDrawer ? " td-nav-auth--drawer" : ""}${className ? ` ${className}` : ""}`;

  if (!isAuthenticated) {
    return (
      <div className={rootClassName}>
        {hint ? (
          <span className="td-nav-auth__hint" role="status">
            {hint}
          </span>
        ) : null}
        {err ? (
          <span className="td-nav-auth__err" role="alert">
            {err}
          </span>
        ) : null}
        <AuthActionButton
          label="Login"
          className="td-nav-auth__btn td-nav-auth__btn--ghost"
          onClick={() => setAuthOpen(true)}
        />
        <ConnectWalletButton
          label={safeConnectWalletLabel}
          className="td-nav-auth__btn td-nav-auth__btn--gold"
          disabled={busy}
          placement={placement}
          onClick={() => runWalletConnectLogin()}
        />
        <AuthModal
          isOpen={authOpen}
          onClose={() => {
            setAuthOpen(false);
            refreshFromStorage();
          }}
        />
      </div>
    );
  }

  const showIdentity =
  (hasVisibleLabel(shortLinked) || hasVisibleLabel(identity));

  return (
    <div className={rootClassName}>
      {hint ? (
        <span className="td-nav-auth__hint" role="status">
          {hint}
        </span>
      ) : null}
      {err ? (
        <span className="td-nav-auth__err" role="alert">
          {err}
        </span>
      ) : null}
      <Link to={SITE_DASHBOARD_PATH} className="td-nav-auth__btn td-nav-auth__btn--ghost td-nav-auth__btn--dash">
        Dashboard
      </Link>
      {needsLinkWallet ? (
        <AuthActionButton
          label={safeLinkWalletLabel}
          className="td-nav-auth__btn td-nav-auth__btn--gold"
          disabled={busy}
          onClick={() => runHeaderLinkWallet()}
        />
      ) : null}
      {showIdentity ? (
        <div className="td-nav-auth__identity" title={linked?.address || user?.email || undefined}>
          {hasVisibleLabel(shortLinked) ? <span className="td-nav-auth__mono">{shortLinked}</span> : null}
          {hasVisibleLabel(shortLinked) && hasVisibleLabel(identity) ? (
            <span className="td-nav-auth__identity-sep" aria-hidden>
              {" "}
              ·{" "}
            </span>
          ) : null}
          {hasVisibleLabel(identity) ? <span className="td-nav-auth__user">{identity}</span> : null}
        </div>
      ) : null}
      <AuthActionButton
        label="Logout"
        className="td-nav-auth__btn td-nav-auth__btn--ghost td-nav-auth__btn--logout"
        onClick={() => {
          logout();
          try {
            disconnect?.();
          } catch {
            /* noop */
          }
        }}
      />
    </div>
  );
}
